// One editable component row of the BOM editor.
import { X, Wrench } from '@phosphor-icons/react';
import { Input } from '@/components/ui/input';
import { useT } from '@/i18n/LanguageContext';
import ItemPickerField from '../ItemPickerField';
import { yen, metaFor, unitCostOf } from './bomUtils';

export const BomComponentRow = ({ c, i, items, itemId, excludeIds, canWrite, onPick, onSetRow, onRemove }) => {
  const { t } = useT();
  const m = metaFor(c, items);
  const lineCost = unitCostOf(m) * Number(c.qty_per || 0);
  const short = c.component_item_id && (m.on_hand ?? 0) < Number(c.qty_per || 0);
  return (
    <div className="rounded-lg border border-zinc-200 bg-white p-2" data-testid={`bom-row-${i}`}>
      <div className="flex items-center gap-2">
        <span className="h-6 w-6 shrink-0 flex items-center justify-center rounded-md bg-zinc-100 text-[11px] font-semibold text-zinc-500 tabular-nums" data-testid={`bom-rownum-${i}`}>{i + 1}</span>
        <div className="flex-1 min-w-0">
          <ItemPickerField testid={`bom-component-${i}`} value={c.component_item_id || ''} initialItems={items} excludeId={itemId}
            excludeIds={excludeIds}
            onChange={(id, item) => onPick(i, id, item)} placeholderKey="mfg.bom.component" allowAllDepartments />
        </div>
        <Input type="number" data-testid={`bom-qty-${i}`} value={c.qty_per} onChange={(e) => onSetRow(i, 'qty_per', e.target.value)} className="h-9 w-16 shrink-0 text-right text-xs" placeholder="員数" />
        <div data-testid={`bom-uom-${i}`} title={t('mfg.bom.uomRegistered')}
          className="h-9 w-12 shrink-0 flex items-center justify-center rounded-md border border-zinc-200 bg-zinc-50 text-xs text-zinc-600">
          {c.uom || '—'}
        </div>
        {(m.is_subcontracted || m.processing_type === 'subcontracted') && <Wrench size={14} className="text-orange-500 shrink-0" weight="fill" title={t('mfg.processing.subcontracted')} />}
        {canWrite && <button onClick={() => onRemove(i)} data-testid={`bom-remove-${i}`} className="shrink-0 text-zinc-400 hover:text-rose-600"><X size={16} /></button>}
      </div>
      {c.component_item_id && (
        <div className="mt-1.5 flex items-start gap-2 pl-8" data-testid={`bom-meta-${i}`}>
          <div className="flex flex-1 min-w-0 flex-wrap items-center gap-x-1.5 gap-y-1 text-[11px]">
            {m.sku && <span className="rounded bg-zinc-100 px-1.5 py-0.5 font-mono text-[10px] text-zinc-500">{m.sku}</span>}
            {m.maker_pn && <span className="rounded bg-sky-50 px-1.5 py-0.5 font-mono text-[10px] text-sky-700" title={t('mfg.item.makerPn')}>{m.maker_pn}</span>}
            {m.maker && <span className="text-[10px] text-zinc-400"><span data-no-translate>{m.maker}</span></span>}
            {m.ref_designators && <span className="rounded bg-violet-50 px-1.5 py-0.5 font-mono text-[10px] text-violet-700" title={t('mfg.item.refDes')}>{m.ref_designators}</span>}
            <span data-testid={`bom-stock-${i}`} className={`rounded px-1.5 py-0.5 text-[10px] font-medium tabular-nums ${short ? 'bg-rose-50 text-rose-600' : 'bg-emerald-50 text-emerald-700'}`}>
              {t('mfg.bom.onHand')} {m.on_hand ?? 0} {c.uom || ''}
            </span>
          </div>
          {lineCost > 0 && <span data-testid={`bom-linecost-${i}`} className="shrink-0 text-[11px] tabular-nums text-zinc-500" title={t('mfg.bom.lineCost')}>{yen(lineCost)}</span>}
        </div>
      )}
      {c.component_item_id && (canWrite
        ? <input data-testid={`bom-notes-${i}`} value={c.notes || ''} onChange={(e) => onSetRow(i, 'notes', e.target.value)} placeholder={t('mfg.bom.notesPh')}
            className="mt-1.5 ml-8 block w-[calc(100%-2rem)] h-7 rounded-md border border-zinc-100 bg-zinc-50/60 px-2 text-[11px] text-zinc-600 placeholder:text-zinc-300 focus:outline-none focus:ring-1 focus:ring-orange-200 focus:bg-white" />
        : (c.notes ? <p className="mt-1.5 pl-8 text-[11px] text-zinc-500" data-testid={`bom-notes-${i}`}>{c.notes}</p> : null))}
    </div>
  );
};
